import React from "react";
import styled from "styled-components";

// The easel SVG, with its viewBox dimensions and painting-spot rectangle
// exported so the consumer (ReferencePhoto) can build the outer container
// with the right aspect-ratio and position the painting over the tray.
//
// To tune the easel, edit the SVG lines below AND the EASEL_CANVAS_*
// constants in lockstep — they represent where on the easel the painting
// sits, in this SVG's viewBox units.
export const EASEL_WIDTH = 67;
export const EASEL_HEIGHT = 100;

// Painting area on the easel, as percentages of EASEL_WIDTH / EASEL_HEIGHT.
// ReferencePhoto positions its matte over this rectangle.
export const EASEL_CANVAS_POSITION = {
  left: `${(3 / EASEL_WIDTH) * 100}%`,
  top: `${(5 / EASEL_HEIGHT) * 100}%`,
  width: `${(62 / EASEL_WIDTH) * 100}%`,
  height: `${(66 / EASEL_HEIGHT) * 100}%`,
};

// Brown palette for the easel scenery. Kept as local hex constants — these
// shades don't recur anywhere else in the app, so they don't earn semantic
// tokens. Outline color is driven by `currentColor` (= --color-text) on Svg.
const COLOR_LEG_BACK = "#7a5230";
const COLOR_LEG_FRONT = "#a46b3d";
const COLOR_TRAY = "#d2a27c";

function Easel({ className }) {
  const legs = [
    // back leg
    {
      x1: 33.5,
      y1: 4,
      x2: 33.5,
      y2: 88,
      stroke: COLOR_LEG_BACK,
      strokeWidth: 2,
      strokeLinecap: "round",
    },
    // front left leg
    {
      x1: 20,
      y1: 12,
      x2: 7,
      y2: 94,
      stroke: COLOR_LEG_FRONT,
      strokeWidth: 2.5,
      strokeLinecap: "round",
    },
    // front right leg
    {
      x1: 47,
      y1: 12,
      x2: 60,
      y2: 94,
      stroke: COLOR_LEG_FRONT,
      strokeWidth: 2.5,
      strokeLinecap: "round",
    },
  ];
  const tray = [
    {
      x1: 5,
      y1: 70.5,
      x2: 63,
      y2: 70.5,
      stroke: COLOR_TRAY,
      strokeWidth: 3.5,
      strokeLinecap: "round",
    },
  ];

  // We draw the tray after the legs so that it appears on top.
  // This matters because of the outlines - otherwise the tray
  // outline appears behind the leg foreground, which looks wrong.
  const lineGroups = [legs, tray];

  const outlineWidth = 0.25;

  return (
    <Svg
      className={className}
      viewBox={`0 0 ${EASEL_WIDTH} ${EASEL_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {lineGroups.flatMap((lines) => {
        let outlines = [];
        let fills = [];
        lines.forEach((l, i) => {
          outlines.push(
            <line
              key={`outline-${i}`}
              {...l}
              stroke="currentColor"
              strokeWidth={l.strokeWidth + outlineWidth * 2}
            />,
          );
        });
        lines.forEach((l, i) => {
          fills.push(<line key={`fill-${i}`} {...l} />);
        });
        return [outlines, fills];
      })}
    </Svg>
  );
}

// function Easel({ className }) {
//   return (
//     <Svg
//       className={className}
//       viewBox={`0 0 ${EASEL_WIDTH} ${EASEL_HEIGHT}`}
//       xmlns="http://www.w3.org/2000/svg"
//       aria-hidden="true"
//     >
//       {/* Back leg — darker, set behind the front legs */}
//       <line
//         x1="60"
//         y1="8"
//         x2="60"
//         y2="125"
//         stroke="#7a5230"
//         strokeWidth="3.5"
//         strokeLinecap="round"
//       />
//       {/* Front legs — splay outward from near the top */}
//       <line
//         x1="45"
//         y1="15"
//         x2="25"
//         y2="136"
//         stroke="#a46b3d"
//         strokeWidth="4"
//         strokeLinecap="round"
//       />
//       <line
//         x1="75"
//         y1="15"
//         x2="95"
//         y2="136"
//         stroke="#a46b3d"
//         strokeWidth="4"
//         strokeLinecap="round"
//       />
//       {/* Canvas tray — the ledge the painting rests on */}
//       <line
//         x1="22"
//         y1="104"
//         x2="98"
//         y2="104"
//         stroke="#d2a27c"
//         strokeWidth="4"
//         strokeLinecap="round"
//       />
//     </Svg>
//   );
// }

// Positions itself inside the consumer's Stage so it acts as a decorative
// backdrop; the painting (matte + photo) is positioned on top of it.
const Svg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  /* Drives stroke="currentColor" on the outlines — set explicitly so a
     parent's color rule can't accidentally repaint the easel. */
  color: var(--color-text-muted);
  filter: drop-shadow(0px 8px 6px rgba(0, 0, 0, 0.2));
`;

export default Easel;
